// Pure helpers for placing orders: what the estimate still needs, per-building balances,
// vendor rates for a material, and the next PO numbers.
import {
  quoteBaseRate,
  quoteOptionBaseRate,
  selectedMaterialQuoteForOption,
  type MaterialProcurementOrder,
  type MaterialRateItem,
  type MaterialRequirement,
} from '../pages/OfficeEstimationPage';
import { num } from './estimationUtils';
import { libraryPicks, libraryUnitToOrderUnit, quoteIsGstInclusive, roundMoney, vendorKey, type MaterialPick } from './materialReceiving';

type OrderLike = Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'orderQty' | 'scopeType' | 'scopeId' | 'buildingAllocations' | 'unplannedRejected'>;

const sameMaterial = (order: Pick<OrderLike, 'materialId' | 'quoteOptionId'>, materialId: string, optionId?: string) =>
  order.materialId === materialId && (order.quoteOptionId || '') === (optionId || '');

// ── What the estimate still needs ────────────────────────────────────────────
export function orderedQty(orders: OrderLike[], materialId: string, optionId?: string): number {
  return orders.filter((order) => !order.unplannedRejected && sameMaterial(order, materialId, optionId)).reduce((sum, order) => sum + num(order.orderQty), 0);
}

export type ToOrderBalance = { required: number; ordered: number; toOrder: number };

export function toOrderBalance(requirements: MaterialRequirement[], orders: OrderLike[], materialId: string, optionId?: string): ToOrderBalance {
  const row = requirements.find((candidate) => candidate.scopeType === 'project' && candidate.materialId === materialId && (candidate.quoteOptionId || '') === (optionId || ''));
  const required = num(row?.qty);
  const ordered = orderedQty(orders, materialId, optionId);
  return { required, ordered, toOrder: Math.max(0, Math.round((required - ordered) * 1000) / 1000) };
}

// ── Building-wise balances (used only when an order is split by building) ────
export type BuildingBalance = { id: string; label: string; required: number; ordered: number; balance: number };

export function buildingBalances(requirements: MaterialRequirement[], orders: OrderLike[], materialId: string, optionId?: string): BuildingBalance[] {
  const grouped = new Map<string, MaterialRequirement>();
  for (const row of requirements) {
    if (row.scopeType !== 'building' || row.materialId !== materialId || (row.quoteOptionId || '') !== (optionId || '')) continue;
    const key = row.scopeLabel?.trim().toLowerCase() || row.scopeId || row.key;
    const current = grouped.get(key);
    grouped.set(key, current ? { ...current, qty: num(current.qty) + num(row.qty) } : { ...row, key });
  }
  return Array.from(grouped.values()).map((building) => {
    const ordered = orders
      .filter((order) => !order.unplannedRejected && sameMaterial(order, materialId, optionId))
      .reduce((sum, order) => {
        const allocated = (order.buildingAllocations || []).filter((allocation) => allocation.buildingId === building.scopeId).reduce((total, allocation) => total + num(allocation.qty), 0);
        if (allocated > 0) return sum + allocated;
        return order.scopeType === 'building' && order.scopeId === building.scopeId ? sum + num(order.orderQty) : sum;
      }, 0);
    const required = num(building.qty);
    return { id: building.scopeId || building.key, label: building.scopeLabel || 'Building', required, ordered, balance: Math.max(0, Math.round((required - ordered) * 1000) / 1000) };
  });
}

// ── PO numbers ───────────────────────────────────────────────────────────────
/** Next `count` PO numbers, continuing the pattern already in use (for example PO-0118 -> PO-0119). */
export function suggestPoNumbers(existing: string[], count: number): string[] {
  const seen = new Map<string, { count: number; max: number; width: number }>();
  for (const value of existing) {
    const match = /^(.*?)(\d+)$/.exec(String(value || '').trim());
    if (!match) continue;
    const entry = seen.get(match[1]) || { count: 0, max: 0, width: 0 };
    entry.count += 1;
    entry.max = Math.max(entry.max, parseInt(match[2], 10));
    entry.width = Math.max(entry.width, match[2].length);
    seen.set(match[1], entry);
  }
  let prefix = 'PO-';
  let start = 1;
  let width = 4;
  let best = -1;
  seen.forEach((entry, key) => {
    if (entry.count > best || (entry.count === best && entry.max > start - 1)) { best = entry.count; prefix = key; start = entry.max + 1; width = entry.width; }
  });
  return Array.from({ length: count }, (_, index) => `${prefix}${String(start + index).padStart(width, '0')}`);
}

// ── Vendor rates for a material ──────────────────────────────────────────────
export type VendorRate = {
  quoteId: string; vendor: string; brand: string; code: string;
  /** GST-inclusive rate per order unit, freight not included (0 when the quote has no rate). */
  rate: number;
  /** GST percent and mode saved on the vendor quote; used when displaying typed rates excl/incl GST. */
  gst: number; gstMode: 'inclusive' | 'exclusive' | 'none' | string;
  /** Landed rate per library unit (GST + freight + loading), as saved on an order. */
  snapshot: number;
  rateUnit: string; effectiveFrom: string;
  /** This is the quote currently selected for the material in Rates. */
  selected: boolean;
};

export function vendorRatesFor(item: MaterialRateItem, optionId: string | undefined, unit: string, label: string, date: string): VendorRate[] {
  const selectedId = selectedMaterialQuoteForOption(item, optionId)?.id;
  const pseudoOrder = { unit, materialLabel: label } as MaterialProcurementOrder;
  const rates: VendorRate[] = [];
  for (const quote of item.quotes || []) {
    if ((quote.status || 'active') === 'voided') continue;
    const options = quote.quoteOptions || [];
    const option = optionId ? options.find((candidate) => candidate.id === optionId) : undefined;
    if (optionId && options.length && !option) continue; // this vendor did not quote this variety
    const base = option ? quoteOptionBaseRate(quote, option, date) : quoteBaseRate(quote, date);
    const inclusive = base > 0 ? (quoteIsGstInclusive(quote) ? base : base * (1 + num(quote.gst) / 100)) : 0;
    rates.push({
      quoteId: quote.id,
      vendor: String(quote.vendor || '').trim(),
      brand: option?.brand || quote.brand || item.brand || '',
      code: option?.code || quote.materialCode || '',
      rate: inclusive > 0 ? roundMoney(inclusive * libraryUnitToOrderUnit(pseudoOrder, option || null)) : 0,
      gst: num(quote.gst),
      gstMode: quote.gstMode || 'exclusive',
      snapshot: inclusive > 0 ? roundMoney(inclusive + num(quote.freight) + num(quote.loading)) : 0,
      rateUnit: option?.unit || quote.unit || unit,
      effectiveFrom: quote.effectiveFrom || quote.quoteDate || date,
      selected: quote.id === selectedId,
    });
  }
  return rates.filter((entry) => entry.vendor).sort((a, b) => a.vendor.localeCompare(b.vendor));
}

export function lowestRate(rates: VendorRate[]): VendorRate | undefined {
  return rates.filter((entry) => entry.rate > 0).reduce<VendorRate | undefined>((best, entry) => (!best || entry.rate < best.rate ? entry : best), undefined);
}

/** The vendor to start from: the selected quote, else the cheapest, else the first. */
export function defaultVendorRate(rates: VendorRate[]): VendorRate | undefined {
  return rates.find((entry) => entry.selected) || lowestRate(rates) || rates[0];
}

// ── Varieties that the order catalog does not list ───────────────────────────
/**
 * A material can list its assemblies and varieties (for example Sand > Sieved Washed Bodeli) without
 * having vendor-quote options for them. Receiving lists those, so ordering must too. Returns those
 * library picks that the order catalog has no row for.
 */
export function varietyPicksMissingFromCatalog(
  items: MaterialRateItem[],
  catalogRows: Array<Pick<MaterialRequirement, 'materialId' | 'quoteOptionId' | 'label'>>,
): MaterialPick[] {
  const withOptions = new Set(catalogRows.filter((row) => row.quoteOptionId).map((row) => row.materialId));
  const normal = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');
  const known = new Set(catalogRows.map((row) => `${row.materialId}|${normal(row.label)}`));
  return libraryPicks(items).filter((pick) => {
    if (withOptions.has(pick.item.id)) return false; // its varieties are already listed through their quote options
    const distinct = !!pick.variety || normal(pick.assemblyType) !== normal(pick.item.materialName);
    return distinct && !known.has(`${pick.item.id}|${normal(pick.label)}`);
  });
}

// ── Which vendors this project has already used for a material ───────────────
export type VendorUse = { vendor: string; lastDate: string; lastPo: string; count: number; exact: boolean };

/**
 * Vendors this project has ordered (or received) a material from, most relevant first: those used for
 * this exact variety, then those used for another variety of the same material, each newest first.
 */
export function vendorUsage(
  orders: Array<Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'vendor' | 'orderDate' | 'poNumber' | 'unplannedRejected'>>,
  materialId: string,
  optionId?: string,
): VendorUse[] {
  const byVendor = new Map<string, VendorUse>();
  const lastByVendor = new Map<string, string>();
  for (const order of orders) {
    const vendor = String(order.vendor || '').trim();
    if (!vendor || order.unplannedRejected || order.materialId !== materialId) continue;
    const key = vendorKey(vendor);
    const exact = (order.quoteOptionId || '') === (optionId || '');
    const date = String(order.orderDate || '');
    const current = byVendor.get(key);
    if (!current) { byVendor.set(key, { vendor, lastDate: date, lastPo: order.poNumber || '', count: 1, exact }); lastByVendor.set(key, date); continue; }
    current.count += 1;
    current.exact = current.exact || exact;
    if (date >= (lastByVendor.get(key) || '')) { current.lastDate = date; current.lastPo = order.poNumber || current.lastPo; current.vendor = vendor; lastByVendor.set(key, date); }
  }
  return Array.from(byVendor.values()).sort((a, b) => Number(b.exact) - Number(a.exact) || b.lastDate.localeCompare(a.lastDate));
}

// ── Estimating a truck's size from history (tentative-precision materials: sand, aggregate) ──────
type OrderWithReceipts = Pick<MaterialProcurementOrder, 'materialId' | 'vendor' | 'receipts' | 'unplannedRejected'>;

function receiptQuantities(orders: OrderWithReceipts[], materialId: string, vendor?: string): number[] {
  const key = vendor ? vendorKey(vendor) : '';
  const quantities: number[] = [];
  for (const order of orders) {
    if (order.materialId !== materialId || order.unplannedRejected) continue;
    if (vendor && vendorKey(order.vendor) !== key) continue;
    for (const receipt of order.receipts || []) {
      const qty = num(receipt.qty);
      if (qty > 0) quantities.push(qty);
    }
  }
  return quantities;
}
const average = (values: number[]) => Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 1000) / 1000;

export type TruckSizeEstimate = { perTruck: number; source: 'vendor' | 'allVendors' | 'none' };

/**
 * A rough "quantity per truck" for ordering a tentative-precision material by the truckload — no two
 * trucks carry exactly the same load, so this is the all-time average of what actually arrived, not a
 * fixed conversion. Prefers this vendor's own delivery history; falls back to every vendor's average
 * when this one has none yet, and to no estimate at all (type the total directly) with no history.
 */
export function averageTruckSize(orders: OrderWithReceipts[], materialId: string, vendor: string): TruckSizeEstimate {
  const own = receiptQuantities(orders, materialId, vendor);
  if (own.length) return { perTruck: average(own), source: 'vendor' };
  const all = receiptQuantities(orders, materialId);
  if (all.length) return { perTruck: average(all), source: 'allVendors' };
  return { perTruck: 0, source: 'none' };
}
